import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck } from "@phosphor-icons/react";
import { CONTACT } from "@/lib/api";

const TERMS = [
  {
    title: "Replacement Policy",
    body: "Quick Maid Services Pune will provide up to 3 replacement candidates within 12 months from the date of the first candidate's joining, subject to company policy.",
  },
  {
    title: "Registration Charges",
    body: "Client registration charges are non-refundable and are separate from the service subscription fee.",
  },
  {
    title: "Service Fee",
    body: "The company service fee must be paid on or before the maid's first working day unless otherwise agreed in writing.",
  },
  {
    title: "Verification",
    body: "Police Verification and Agency Verification will be provided after the complete payment has been received by the company.",
  },
  {
    title: "Candidate Selection",
    body: "Quick Maid Services Pune verifies candidates to the best of its ability. However, the final selection and supervision of the employee are the sole responsibility of the client.",
  },
  {
    title: "Salary Payment",
    body: "The salary of the maid or any staff member shall be decided and paid directly by the client. The company is not responsible for salary negotiations or payment disputes.",
  },
  {
    title: "Payment for Days Worked",
    body: "The client must pay the agreed salary for all days worked by the employee. Failure to pay may result in immediate termination of services.",
  },
  {
    title: "Direct Hiring",
    body: "If the client hires or continues the services of a candidate without informing Quick Maid Services Pune or without clearing all pending payments, the company will not be responsible for any future disputes, accidents, or losses.",
  },
  {
    title: "Travel Expenses",
    body: "Any travel, transportation, or transit expenses of the candidate shall be borne by the client.",
  },
  {
    title: "Safety & Belongings",
    body: "The client has the right to check the belongings of the employee at the time of joining or leaving. The company will not be responsible for any theft, loss, or damage reported after the employee has left the job.",
  },
  {
    title: "Police Assistance",
    body: "In case of any police complaint involving a candidate provided by Quick Maid Services Pune, the company will cooperate with the authorities and the client as required.",
  },
  {
    title: "No Refund Policy",
    body: "If the client voluntarily cancels or discontinues the company's services after registration or placement, no registration fee or service fee will be refunded.",
  },
  {
    title: "Client Responsibility",
    body: "The client agrees to provide a safe and respectful working environment and comply with all applicable labour laws.",
  },
  {
    title: "Jurisdiction",
    body: "Any dispute arising from these services shall be subject to the jurisdiction of the courts in Pune, Maharashtra.",
  },
  {
    title: "Acceptance",
    body: "By booking or availing our services, the client confirms that they have read, understood, and accepted all the above terms and conditions.",
  },
];

export default function Terms() {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      {/* Top strip */}
      <div className="bg-[var(--navy)] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-9 flex items-center text-xs md:text-sm">
          <Link to="/" className="flex items-center gap-2 font-semibold hover:text-[var(--gold)] transition-colors" data-testid="terms-back-home">
            <ArrowLeft size={14} weight="bold" />
            Back to home
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden bg-[var(--paper)] border-b border-slate-200">
        <div className="pointer-events-none absolute -right-32 -top-24 h-96 w-96 rounded-full bg-[var(--gold)]/15 blur-3xl" />
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-24 relative">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-11 w-11 rounded-2xl bg-[var(--gold)] text-[var(--navy)]">
              <ShieldCheck weight="fill" size={22} />
            </span>
            <p className="label-caps text-[var(--gold)]">Legal</p>
          </div>
          <h1 className="font-display text-4xl md:text-6xl text-[var(--navy)] tracking-tight leading-[1.05] mt-4">
            Terms &amp; Conditions
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
            Please read the following terms carefully before availing services from
            Quick Maid Services Pune.
          </p>
          <p className="text-xs text-slate-400 mt-2 uppercase tracking-wider">
            Last updated: March 2026
          </p>
        </div>
      </section>

      {/* Terms body */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <ol className="space-y-6 counter-reset">
          {TERMS.map((t, i) => (
            <motion.li
              key={t.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: (i % 4) * 0.05 }}
              className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 hover:shadow-md transition-shadow"
              data-testid={`term-${i + 1}`}
            >
              <div className="flex items-start gap-4">
                <span className="shrink-0 grid place-items-center h-9 w-9 rounded-full bg-[var(--navy)] text-white text-sm font-bold">
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-display text-xl md:text-2xl text-[var(--navy)] tracking-tight">
                    {t.title}
                  </h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{t.body}</p>
                </div>
              </div>
            </motion.li>
          ))}
        </ol>

        {/* Contact card */}
        <div className="mt-16 rounded-3xl bg-[var(--navy)] text-white p-8 md:p-10 grain relative overflow-hidden">
          <div className="pointer-events-none absolute -bottom-16 -right-16 h-64 w-64 rounded-full bg-[var(--gold)]/15 blur-3xl" />
          <p className="label-caps text-[var(--gold)]">Need clarification?</p>
          <h2 className="font-display text-2xl md:text-3xl mt-2 leading-tight">
            Reach out to us anytime.
          </h2>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href={`tel:+${CONTACT.phoneIntl}`}
              className="rounded-full bg-[var(--gold)] text-[var(--navy)] font-bold px-6 py-3 hover:bg-[var(--gold-soft)] transition-colors"
              data-testid="terms-contact-call"
            >
              Call +91 {CONTACT.phone}
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              className="rounded-full border border-white/20 text-white font-bold px-6 py-3 hover:bg-white/10 transition-colors"
              data-testid="terms-contact-email"
            >
              Email us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[var(--navy-deep)] text-white/70 py-8" data-testid="terms-footer">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between text-sm">
          <p>&copy; {new Date().getFullYear()} Quick Maid Services Pune.</p>
          <Link to="/" className="hover:text-[var(--gold)] transition-colors">Home</Link>
        </div>
      </footer>
    </div>
  );
}
